import { useState, useMemo } from 'react';
import { Chip, Icon, Link, Text } from '@nimbus-ds/components';
import { EditIcon } from '@nimbus-ds/icons';
import { Stack } from 'commons/components';
import useTranslationShipping from 'domains/Shipping/useTranslationShipping';

interface SelectedItemsChipListProps {
  items: (string | number)[];
  editLinkText: string;
  getItemText: (item: string | number) => string;
  onEdit: () => void;
  onRemove: (item: string | number) => void;
}

const MAX_VISIBLE_ITEMS = 10;

function SelectedItemsChipList({
  items,
  editLinkText,
  getItemText,
  onEdit,
  onRemove,
}: Readonly<SelectedItemsChipListProps>): JSX.Element {
  const t = useTranslationShipping(
    'deliveryMethods.shippingLimiting.modals.common',
  );
  const [showAll, setShowAll] = useState(false);

  const hasMoreItems = items.length > MAX_VISIBLE_ITEMS;

  const visibleItems = useMemo(() => {
    if (showAll || !hasMoreItems) {
      return items;
    }
    return items.slice(0, MAX_VISIBLE_ITEMS);
  }, [items, showAll, hasMoreItems]);

  const hiddenCount = items.length - MAX_VISIBLE_ITEMS;

  return (
    <Stack column align="stretch" spacing="base">
      <Stack wrap spacing="tight">
        {visibleItems.map((item) => (
          <Chip
            key={item}
            id={item.toString()}
            text={getItemText(item)}
            removable
            onClick={() => {
              onRemove(item);
            }}
          />
        ))}
        {hasMoreItems && !showAll && (
          <Link as="button" onClick={() => setShowAll(true)}>
            <Text color="neutral-textLow">
              {t('showMore', { count: hiddenCount })}
            </Text>
          </Link>
        )}
        {hasMoreItems && showAll && (
          <Link as="button" onClick={() => setShowAll(false)}>
            <Text color="neutral-textLow">{t('showLess')}</Text>
          </Link>
        )}
      </Stack>
      <Link as="a" textDecoration="none" onClick={onEdit}>
        <Icon color="primary-interactive" source={<EditIcon />} />
        <Text color="primary-interactive">{editLinkText}</Text>
      </Link>
    </Stack>
  );
}

export default SelectedItemsChipList;
